import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Link as ChakraLink,
  useColorModeValue,
  Badge,
  SimpleGrid,
  Icon,
} from '@chakra-ui/react'
import { FiLink, FiShield, FiGlobe, FiLock } from 'react-icons/fi'
import Balancer from 'react-wrap-balancer'
import { useAccount } from 'wagmi'

import { ConnectWallet } from '@/components'
import { useIsClient } from '@/hooks'

const CrossChain = () => {
  const isClient = useIsClient()
  const { isConnected } = useAccount()
  const patentTextColor = useColorModeValue('slate.600', 'slate.400')
  const patentLinkColor = useColorModeValue('brand.700', 'brand.300')
  const cardBg = useColorModeValue('white', 'slate.800')
  const cardBorder = useColorModeValue('slate.200', 'slate.700')

  if (!isConnected && isClient) {
    return <ConnectWallet />
  }

  return (
    <Box w="full">
      <Flex
        textAlign="center"
        flexDir="column"
        justifyContent="center"
        alignItems="center"
        my="12"
        mx="auto"
        maxW="xl"
      >
        <Badge colorScheme="purple" fontSize="sm" px={3} py={1} mb={4}>
          Cross-Chain
        </Badge>
        <Heading mt="2" zIndex="1">
          <Balancer>Cross-Chain Asset Management</Balancer>
        </Heading>
        <Text mt="3" zIndex="1" color="slate.600">
          <Balancer>
            Cryptographically-bound surrogate tokens that enable low-friction, low-cost proof of
            ownership across blockchain networks without requiring asset migration or cross-chain
            bridges.
          </Balancer>
        </Text>
        <Text fontSize="xs" color={patentTextColor} mt="2" zIndex="1">
          Protected by{' '}
          <ChakraLink
            href="/patents"
            color={patentLinkColor}
            textDecoration="none"
            _hover={{ textDecoration: 'underline' }}
          >
            U.S. Patent No. 12,293,358
          </ChakraLink>
        </Text>
      </Flex>

      <Container maxW="4xl" my={{ base: 8, md: 12 }}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <FeatureCard
            icon={FiLink}
            title="Cross-Chain Proof"
            description="Prove ownership of assets on one chain while participating in activities on another, eliminating security risks of traditional bridges."
            cardBg={cardBg}
            cardBorder={cardBorder}
          />
          <FeatureCard
            icon={FiShield}
            title="No Asset Migration"
            description="Your original assets stay secure in their native chain. Surrogate tokens represent ownership without transfer risks."
            cardBg={cardBg}
            cardBorder={cardBorder}
          />
          <FeatureCard
            icon={FiGlobe}
            title="Gaming & Token-Gating"
            description="Enable secure token-gating, access control, and community moderation across decentralized gaming ecosystems."
            cardBg={cardBg}
            cardBorder={cardBorder}
          />
          <FeatureCard
            icon={FiLock}
            title="Low-Cost Verification"
            description="Verify ownership without the gas costs and complexity of cross-chain asset transfers."
            cardBg={cardBg}
            cardBorder={cardBorder}
          />
        </SimpleGrid>

      </Container>

      <Container maxW="4xl" mb={12}>
        <Box
          p={8}
          borderWidth="1px"
          borderRadius="lg"
          borderColor={cardBorder}
          bg={cardBg}
          textAlign="center"
        >
          <Text fontSize="lg" fontWeight="medium">
            Contact{' '}
            <ChakraLink
              href="mailto:sales@foolproof.xyz"
              color={patentLinkColor}
              _hover={{ textDecoration: 'underline' }}
            >
              sales@foolproof.xyz
            </ChakraLink>
            {' '}to get started
          </Text>
        </Box>
      </Container>
    </Box>
  )
}

interface FeatureCardProps {
  icon: React.ElementType
  title: string
  description: string
  cardBg: string
  cardBorder: string
}

const FeatureCard = ({ icon, title, description, cardBg, cardBorder }: FeatureCardProps) => {
  return (
    <Box p={6} borderWidth="1px" borderRadius="lg" borderColor={cardBorder} bg={cardBg}>
      <Flex align="center" mb={3}>
        <Icon as={icon} boxSize={6} color="brand.500" mr={3} />
        <Heading size="sm">{title}</Heading>
      </Flex>
      <Text fontSize="sm" color="slate.600">
        {description}
      </Text>
    </Box>
  )
}

export default CrossChain
